import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase } from '../lib/supabase'
import type { Database } from '../lib/supabase'
// import { useAuthStore } from './authStore'
import logger from '../utils/logger'
import { useFamilyStore } from './familyStore'

type EventRow = Database extends { public: { Tables: { events: any } } } 
  ? Database['public']['Tables']['events']['Row'] 
  : {
      id: string
      family_id: string
      child_id: string
      title: string
      notes: string | null
      start_at: string
      end_at: string | null
      all_day: boolean
      location: string | null
      parents_only: boolean
      // rozszerzone pola typów (zachowaj 'event' jako legacy/fallback)
      type: 'note' | 'expense' | 'event'
      amount: number | null
      currency: string | null
      metadata: Record<string, any> | null
      created_by: string
      created_at: string
      updated_at: string
    }

interface EventsState {
  events: EventRow[]
  isLoading: boolean
  error: string | null
  lastFetch: Record<string, number>
  inFlight: Record<string, boolean>
  latestKey: string | null
  eventsByKey: Record<string, EventRow[]>
  fetchEvents: (childId: string, startISO: string, endISO: string) => Promise<void>
  addEvent: (payload: Omit<EventRow, 'id' | 'created_at' | 'updated_at'>) => Promise<void>
  updateEvent: (id: string, updates: Partial<EventRow>) => Promise<void>
  deleteEvent: (id: string) => Promise<void>
  clearCache: () => void
}

export const useEventsStore = create<EventsState>()(
  persist(
    (set, get) => ({
      events: [],
      isLoading: false,
      error: null,
      lastFetch: {},
      inFlight: {},
      latestKey: null,
      eventsByKey: {},

      fetchEvents: async (childId, startISO, endISO) => {
        const key = `${childId}-${startISO}-${endISO}`
        const last = get().lastFetch[key] || 0
        const now = Date.now()
        if (now - last < 5000) {
          const cached = get().eventsByKey[key]
          if (cached) {
            // Użyj pamięci podręcznej per zakres
            set(state => ({ ...state, events: cached, latestKey: key }))
            logger.debug('📦 Używam cached events for key:', key)
            return
          }
          // Brak per-key cache – jednak pobierz
          logger.debug('🔄 Cache fresh but no per-key data; fetching:', key)
        }
        // Deduplikacja dla tego samego klucza
        if (get().inFlight[key]) return

        set(state => ({
          ...state,
          isLoading: true,
          error: null,
          inFlight: { ...state.inFlight, [key]: true },
          latestKey: key
        }))
        try {
          const isUuid = (v: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v)

          // Agregacja po family_id (Wszystkie dzieci)
          if (childId === 'all-children') {
            const { family } = useFamilyStore.getState()
            if (!family) {
              logger.debug('⏭️ Brak rodziny – pomijam pobranie events (all-children)')
              set(state => ({
                ...state,
                inFlight: Object.fromEntries(Object.entries(state.inFlight).filter(([k]) => k !== key)),
                isLoading: false,
                lastFetch: { ...state.lastFetch, [key]: now }
              }))
              return
            }

            const { data, error } = await supabase
              .from('events')
              .select('*')
              .eq('family_id', family.id)
              .gte('start_at', startISO)
              .lte('start_at', endISO)
              .order('start_at')

            if (error) throw error

            if (get().latestKey === key) {
              set(state => ({
                ...state,
                events: data as EventRow[],
                isLoading: false,
                lastFetch: { ...state.lastFetch, [key]: now },
                eventsByKey: { ...state.eventsByKey, [key]: data as EventRow[] }
              }))
            } else {
              // Zaktualizuj cache nawet jeśli to nie najnowszy klucz
              set(state => ({ ...state, eventsByKey: { ...state.eventsByKey, [key]: data as EventRow[] }, lastFetch: { ...state.lastFetch, [key]: now } }))
            }
            set(state => ({ ...state, inFlight: Object.fromEntries(Object.entries(state.inFlight).filter(([k]) => k !== key)) }))
            return
          }

          // Jeśli childId jest tymczasowy lub nie jest UUID, nie wykonujemy zapytania
          if (!childId || childId === 'temp-child-id' || !isUuid(childId)) {
            logger.debug('⏭️ Pomijam pobieranie events – niewłaściwy childId:', childId)
            set(state => ({
              ...state,
              inFlight: Object.fromEntries(Object.entries(state.inFlight).filter(([k]) => k !== key)),
              isLoading: false,
              lastFetch: { ...state.lastFetch, [key]: now }
            }))
            return
          }

          const { data, error } = await supabase
            .from('events')
            .select('*')
            .eq('child_id', childId)
            .gte('start_at', startISO)
            .lte('start_at', endISO)
            .order('start_at')

          if (error) throw error

          if (get().latestKey === key) {
            set(state => ({
              ...state,
              events: data as EventRow[],
              isLoading: false,
              lastFetch: { ...state.lastFetch, [key]: now },
              eventsByKey: { ...state.eventsByKey, [key]: data as EventRow[] }
            }))
          } else {
            set(state => ({ ...state, eventsByKey: { ...state.eventsByKey, [key]: data as EventRow[] }, lastFetch: { ...state.lastFetch, [key]: now } }))
          }
        } catch (e: any) {
          logger.error('❌ Błąd pobierania events:', e)
          set(state => ({ ...state, error: e.message || 'Błąd pobierania wydarzeń', isLoading: false }))
        } finally {
          set(state => ({ ...state, inFlight: Object.fromEntries(Object.entries(state.inFlight).filter(([k]) => k !== key)) }))
        }
      },

      addEvent: async (payload) => {
        const optimisticId = `optimistic-${Date.now()}`
        const optimistic = {
          id: optimisticId as any,
          ...payload,
          created_at: new Date().toISOString() as any,
          updated_at: new Date().toISOString() as any,
        } as EventRow
        set(state => ({ ...state, events: [...state.events, optimistic] }))

        try {
          const { data, error } = await supabase
            .from('events')
            .insert(payload as any)
            .select()
          if (error) throw error
          set(state => ({
            ...state,
            events: state.events.map(ev => ev.id === optimisticId ? (data![0] as EventRow) : ev)
          }))
        } catch (e) {
          logger.error('❌ addEvent error:', e)
          set(state => ({ ...state, events: state.events.filter(ev => ev.id !== optimisticId) }))
        }
      },

      updateEvent: async (id, updates) => {
        let previous: EventRow | null = null
        set(state => ({
          ...state,
          events: state.events.map(ev => {
            if (ev.id === id) { previous = { ...ev }; return { ...ev, ...updates, updated_at: new Date().toISOString() as any } }
            return ev
          })
        }))
        try {
          const { error } = await supabase
            .from('events')
            .update(updates)
            .eq('id', id)
          if (error) throw error
        } catch (e) {
          logger.error('❌ updateEvent error:', e)
          if (previous) set(state => ({ ...state, events: state.events.map(ev => ev.id === id ? previous as EventRow : ev) }))
        }
      },

      deleteEvent: async (id) => {
        let removed: EventRow | null = null
        set(state => ({
          ...state,
          events: state.events.filter(ev => { if (ev.id === id) { removed = ev; return false } return true })
        }))
        try {
          const { error } = await supabase
            .from('events')
            .delete()
            .eq('id', id)
          if (error) throw error
        } catch (e) {
          logger.error('❌ deleteEvent error:', e)
          if (removed) set(state => ({ ...state, events: [...state.events, removed as EventRow] }))
        }
      },

      clearCache: () => set(state => ({ ...state, events: [], lastFetch: {}, inFlight: {}, latestKey: null, eventsByKey: {} }))
    }),
    {
      name: '2domy-events-storage',
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      partialize: (_state) => ({})
    }
  )
)

export type { EventRow as CalendarEvent }


